import React, { useState } from "react";
import { Card, Modal } from "reactstrap";

import AppointmentsService from "services/appointments";
import StorageService from "services/StorageService";
import { useSearchDoctors } from "hooks/useSearchDoctors";
import { displayUkDate, sessionUkTimes } from "helpers/ukDate";
import { USER } from "constants/storageConstants";
import { ADMIN } from "constants/roles";

import { CustomSearchInput } from "../searchField/CustomSearchInput";

const COULD_NOT_MOVE = "The session could not be moved. Please try again.";

const CANCELLED_STATUS = 7;

export const MOVE_SESSION_NOTICE = "Patients are not told; their bookings and video links stay as they are. Both doctors get an email.";

/** When the session's doctor now is deleted or blocked: the server emails only the new doctor. */
export const MOVE_SESSION_NOTICE_NEW_DOCTOR_ONLY = "Patients are not told; their bookings and video links stay as they are. The new doctor gets an email.";

/**
 * Whether the server will email the session's doctor now: not when their account is deleted or blocked
 * (it skips those), nor when it has no address (a deleted account's is cleared - the session's doctor
 * carries isBlocked and email, not isDeleted). Review L3: the sentence follows who is actually emailed.
 *
 * @param {object} session
 * @returns {boolean}
 */
export const oldDoctorIsEmailed = (session) => {
    const doctor = session && session.doctor;
    return Boolean(doctor) && !doctor.isBlocked && !doctor.isDeleted && Boolean(doctor.email);
};

/** What the confirmation says patients and doctors are told. */
export const moveNoticeFor = session => (oldDoctorIsEmailed(session) ? MOVE_SESSION_NOTICE : MOVE_SESSION_NOTICE_NEW_DOCTOR_ONLY);

/** The session's doctor as the card shows them: first and last name. */
export const doctorNameOf = (session) => {
    const doctor = session && session.doctor;
    return doctor ? `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim() : "";
};

/** The bookings on the session as the card shows them: every slot with a patient, not cancelled. */
export const bookingCountOf = session => ((session && session.appointments) || [])
    .filter(slot => slot && slot.patient && slot.status !== CANCELLED_STATUS)
    .length;

const bookingsText = count => (count === 1 ? "1 booking" : `${count} bookings`);

/**
 * The doctors the session can go to: anyone in the list but its own doctor - neither the doctors row it
 * has now, nor another doctors row of the same person (the server refuses both too); never a deleted or
 * blocked account (the server refuses those: review L6 - this picker only, Create Session's list is as
 * it was); and, for an Admin, never their own account (the server refuses that too: review M1 - a super
 * admin may).
 *
 * @param {object[]} options  useSearchDoctors' options ({ value: doctorId, label, userId, isDeleted, isBlocked })
 * @param {object} session
 * @param {(object|null)} signedIn  the stored user ({ id: users.id, role })
 * @returns {object[]}
 */
export function otherDoctorOptions(options, session, signedIn) {
    const currentUserId = session && session.doctor ? session.doctor.id : undefined;
    const ownUserId = signedIn && signedIn.role === ADMIN ? signedIn.id : undefined;

    return options.filter(option => option.value
        && option.value !== session.doctorId
        && (currentUserId === undefined || option.userId !== currentUserId)
        && !option.isDeleted
        && !option.isBlocked
        && (ownUserId === undefined || option.userId !== ownUserId));
}

/**
 * "Move to another doctor" for one session - super admins and Admins (the server enforces it). Moves the
 * WHOLE session, as it is: its times, slots and bookings stay; only the doctor changes.
 *
 * Two steps: choose the doctor, then a confirmation showing the day and UK times, from -> to, the number
 * of bookings and that patients are not told. It sends the new doctor and the session's doctor as this
 * page shows it (expectedFromDoctorId), so a session someone else moved meanwhile is refused, not moved
 * again. The server's answer - e.g. that the new doctor already has a session then - is shown here, in
 * the modal, which stays open.
 */
export default function MoveSessionModal({ session, onClose, onMoved }) {
    const { handleDoctorSearch, doctorsOptions } = useSearchDoctors();
    const [chosen, setChosen] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const times = sessionUkTimes(session);
    const when = `${displayUkDate(times.date)}, ${times.startsAt} - ${times.endsAt} (UK time)`;
    const fromName = doctorNameOf(session);
    const options = otherDoctorOptions(doctorsOptions, session, new StorageService().getValueByKey(USER));
    const bookings = bookingCountOf(session);

    const move = async () => {
        setSaving(true);
        setError("");
        try {
            const response = await AppointmentsService.moveAppointmentSession(session.id, {
                toDoctorId: chosen.value,
                expectedFromDoctorId: session.doctorId,
            });
            onMoved(response && response.data, chosen);
        } catch (failure) {
            setError((failure && failure.message) || COULD_NOT_MOVE);
            setSaving(false);
        }
    };

    return (
        <Modal isOpen className="modal-size" centered>
            <Card className="p-4 mb-0" data-testid="move-session-modal">
                <header className="font-size-22">Move to another doctor</header>

                {!confirming ? (
                    <div className="mt-4">
                        <p className="mb-1" data-testid="move-session-when">{`Session: ${when}`}</p>
                        <p className="mb-3" data-testid="move-session-from">{`Doctor now: ${fromName}`}</p>

                        <label className="d-block mb-1">Move it to</label>
                        <CustomSearchInput
                            handleSearch={handleDoctorSearch}
                            options={options}
                            initialValue={chosen}
                            handleChange={selected => setChosen(selected && selected.value ? selected : null)}
                            placeholder="Select a doctor"
                            isDisabled={saving}
                        />

                        <section className="d-flex flex-row justify-content-between">
                            <button
                                className="btn btn-primary w-100 mt-4 mr-3"
                                data-testid="move-session-continue"
                                disabled={!chosen}
                                onClick={() => setConfirming(true)}
                            >
                                Continue
                            </button>
                            <button className="btn btn-outline-primary w-100 mt-4 ml-3" onClick={onClose}>Cancel</button>
                        </section>
                    </div>
                ) : (
                    <div className="mt-4" data-testid="move-session-confirm-step">
                        <ul className="list-unstyled mb-0" data-testid="move-session-summary">
                            <li>{`Session: ${when}`}</li>
                            <li>{`Doctor: ${fromName} → ${chosen.label}`}</li>
                            <li>{`Bookings: ${bookingsText(bookings)}`}</li>
                        </ul>
                        <p className="mt-3 mb-0" data-testid="move-session-notice">{moveNoticeFor(session)}</p>
                        {error ? <p className="text-danger mt-3 mb-0" role="alert" data-testid="move-session-error">{error}</p> : null}

                        <section className="d-flex flex-row justify-content-between">
                            <button
                                className="btn btn-primary w-100 mt-4 mr-3"
                                data-testid="move-session-confirm"
                                disabled={saving}
                                onClick={move}
                            >
                                Move session
                            </button>
                            <button
                                className="btn btn-outline-primary w-100 mt-4 ml-3"
                                disabled={saving}
                                onClick={() => { setError(""); setConfirming(false); }}
                            >
                                Back
                            </button>
                        </section>
                    </div>
                )}
            </Card>
        </Modal>
    );
}
